import { IntegrationPackages } from "./IntegrationPackages";
import { useEsmAccess } from "./FeatureAccess";
import { StandaloneFormData } from "./StandaloneFormData";
import { FormConfigurationSession } from "@proof/sdk";
import { IntegrationAccess } from "./IntegrationAccess";
import { useEffect, useMemo, useState } from "react";
import type { Project, Repository, Revision } from "@proof/core";
import { summarizeProject, type ProjectCatalogPage } from "@proof/core";
import { Select, Control, SearchableSelect, type SelectLoader } from "./components";
import { Integrations } from "./Integrations";
import { FormConfigurationEditor } from "@proof/authoring";
import { EsmProjectIntegration } from "./EsmProjectIntegration";

/**
 * Integrations live in the shared library, not inside one project. Choose the
 * project and, optionally, a saved version to refine what each integration
 * (embeds, handoff, API, data transforms) targets.
 */
export function LibraryIntegrations({
  projects,
  repository,
  tenantId,
  initialProjectId,
  onImport,
  notify,
}: {
  projects: Project[];
  repository: Repository;
  tenantId: string;
  initialProjectId?: string;
  onImport: (projects: Project[]) => void | Promise<void>;
  notify: (message: string) => void;
}) {
  const esmAllowed = useEsmAccess();
  const [formDefinition, setFormDefinition] = useState(
    () => new FormConfigurationSession().value,
  );
  const [access, setAccess] = useState(false);
  const [esmEditor, setEsmEditor] = useState(false);
  const [standalone, setStandalone] = useState(
    () => projects.length === 0 && !repository.catalog,
  );
  const [projectId, setProjectId] = useState(
    () => initialProjectId || projects[0]?.id || "",
  );
  const [revisions, setRevisions] = useState<Revision[]>([]),
    [revisionId, setRevisionId] = useState("");
  const [choices, setChoices] = useState<ProjectCatalogPage>({
    items: projects.map(summarizeProject),
    total: projects.length,
    offset: 0,
    limit: 50,
  });
  const [catalogBusy, setCatalogBusy] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [selectedProject, setSelectedProject] = useState<Project>();
  const [projectBusy, setProjectBusy] = useState(false);
  const [projectError, setProjectError] = useState("");
  useEffect(() => {
    if (initialProjectId) setProjectId(initialProjectId);
  }, [initialProjectId]);
  useEffect(() => {
    if (!repository.catalog) {
      setChoices({
        items: projects.map(summarizeProject),
        total: projects.length,
        offset: 0,
        limit: 50,
      });
      return;
    }
    // The first page only chooses a default project and detects an empty
    // workspace; the Project dropdown searches the full catalog itself.
    let active = true;
    setCatalogBusy(true);
    setCatalogError("");
    void repository.catalog!(tenantId, { status: "all", limit: 1, offset: 0 })
      .then((page) => {
        if (!active) return;
        setChoices(page);
        setProjectId((value) => value || page.items[0]?.id || "");
        if (!page.total) setStandalone(true);
      })
      .catch((e) => {
        if (active) setCatalogError((e as Error).message);
      })
      .finally(() => {
        if (active) setCatalogBusy(false);
      });
    return () => {
      active = false;
    };
  }, [repository, tenantId, projects]);
  const loadProjects = useMemo<SelectLoader | undefined>(
    () =>
      repository.catalog
        ? async (query, offset, limit) => {
            const page = await repository.catalog!(tenantId, {
              status: "all",
              limit,
              offset,
              search: query,
            });
            return {
              items: page.items.map((row) => ({
                value: row.id,
                label: row.name,
              })),
              total: page.total,
            };
          }
        : undefined,
    [repository, tenantId],
  );
  useEffect(() => {
    let active = true;
    setProjectError("");
    const cached = projects.find((project) => project.id === projectId);
    if (cached) {
      setSelectedProject(cached);
      setProjectBusy(false);
      return;
    }
    if (!projectId) {
      setSelectedProject(undefined);
      setProjectBusy(false);
      return;
    }
    setProjectBusy(true);
    void repository
      .get(tenantId, projectId)
      .then((project) => {
        if (active) {
          setSelectedProject(project);
          if (!project) setProjectError("The selected project is unavailable.");
        }
      })
      .catch((e) => {
        if (active) setProjectError((e as Error).message);
      })
      .finally(() => {
        if (active) setProjectBusy(false);
      });
    return () => {
      active = false;
    };
  }, [projectId, projects, repository, tenantId]);
  useEffect(() => {
    if (!projectId && projects[0]) setProjectId(projects[0].id);
  }, [projects, projectId]);
  useEffect(() => {
    let active = true;
    setRevisions([]);
    setRevisionId("");
    if (!projectId) return;
    void repository
      .history(tenantId, projectId)
      .then((rows) => active && setRevisions(rows))
      .catch(() => active && setRevisions([]));
    return () => {
      active = false;
    };
  }, [projectId, repository, tenantId]);
  const current =
    selectedProject?.id === projectId
      ? selectedProject
      : projects.find((row) => row.id === projectId);
  const revision = revisions.find((row) => row.id === revisionId);
  const project = revision?.definition || current;
  const scope = (
    <section
      className="panel padded integration-scope"
      aria-label="Integration scope"
    >
      <div className="control-grid">
        <Control label="Project">
          <SearchableSelect
            label="Project"
            value={projectId}
            onChange={setProjectId}
            load={loadProjects}
            options={
              loadProjects
                ? undefined
                : choices.items.map((row) => ({ value: row.id, label: row.name }))
            }
            selectedLabel={current?.name}
            placeholder={
              catalogBusy ? "Loading projects…" : "Choose a project"
            }
            searchPlaceholder="Search projects…"
          />
        </Control>
        <Control label="Version">
          <Select
            value={revisionId}
            disabled={!revisions.length}
            onChange={(event) => setRevisionId(event.target.value)}
          >
            <option value="">Current draft</option>
            {revisions.map((row) => (
              <option key={row.id} value={row.id}>
                Revision {row.revision}
                {row.reason ? ` · ${row.reason}` : ""} ·{" "}
                {new Date(row.at).toLocaleDateString()}
              </option>
            ))}
          </Select>
        </Control>
      </div>
      {catalogError && <p role="alert">{catalogError}</p>}
      {projectError && <p role="alert">{projectError}</p>}
      <p className="muted">
        Pick the integration type below. Embeds, handoff and exports use the
        project and version chosen here.
      </p>
    </section>
  );
  return (
    <div className="library-integrations">
      <IntegrationPackages />
      <div className="section-tabs">
        <button
          className={!access && !esmEditor && standalone ? "active" : ""}
          onClick={() => {
            setAccess(false);
            setEsmEditor(false);
            setStandalone(true);
          }}
        >
          Form configuration plugin
        </button>
        <button
          className={!access && !esmEditor && !standalone ? "active" : ""}
          onClick={() => {
            setAccess(false);
            setStandalone(false);
            setEsmEditor(false);
          }}
        >
          Project integrations
        </button>
        {esmAllowed && (
          <button
            className={!access && esmEditor ? "active" : ""}
            onClick={() => {
              setAccess(false);
              setEsmEditor(true);
            }}
          >
            ESM project editor
          </button>
        )}
        <button
          className={access ? "active" : ""}
          onClick={() => setAccess(true)}
        >
          Access &amp; tokens
        </button>
      </div>
      {access ? (
        <IntegrationAccess notify={notify} />
      ) : esmEditor && esmAllowed ? (
        <EsmProjectIntegration />
      ) : standalone ? (
        <>
          <p>
            Build a reusable form here without a project. External data
            bindings, a live form preview, and drop-in setup code follow the
            editor.
          </p>
          <FormConfigurationEditor
            esmEnabled={esmAllowed}
            value={formDefinition}
            onChange={(definition) => setFormDefinition(definition)}
          />
          <StandaloneFormData definition={formDefinition} />
          <details className="panel padded">
            <summary>Embed in your host application</summary>
            <p>
              Load the widget bundle and supply standalone configuration JSON.
              Saving returns your definition without making API requests. Your
              host save callback owns persistence. If it calls the Proof Studio
              API, keep its client secret on your backend and configure B2B
              access for the permissions it needs.
            </p>
            <pre>
              {esmAllowed
                ? `<script src="${window.location.origin}/embed.js"></script>
<proof-form-configuration id="configuration"></proof-form-configuration>
<script>
const editor = document.getElementById("configuration");
editor.value = hostDataset;
// For a dataset with multiple projects:
// editor.options = { projectId: 7 };
editor.onSave = async ({ definition, esm }) => {
  await saveThroughYourHost(esm);
};
</script>`
                : `<script src="${window.location.origin}/embed.js"></script>
<proof-form-configuration id="configuration"></proof-form-configuration>
<script>
const editor = document.getElementById("configuration");
editor.value = hostDefinition;
editor.onSave = async ({ definition }) => saveThroughYourHost(definition);
</script>`}
            </pre>
          </details>
        </>
      ) : project ? (
        <Integrations
          key={`${project.tenantId}:${project.id}:${revisionId}`}
          project={project}
          onImport={onImport}
          notify={notify}
          scope={scope}
          onAccess={() => setAccess(true)}
        />
      ) : (
        <>
          {scope}
          <p>
            {projectBusy || catalogBusy
              ? "Loading selected project…"
              : "Create or import a project to configure document embeds. Access & tokens remains available without a project."}
          </p>
        </>
      )}
    </div>
  );
}
